import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LogOut, Package, ShoppingBag, Plus, Edit, Trash2, Eye, X, Star, Tag } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/ui/toaster';
import { resolveImageUrl } from '../../utils/imageUrl';
import ReviewsManagement from '../../components/admin/ReviewsManagement';
import PromoCodeManagement from '../../components/admin/PromoCodeManagement';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [activeCategory, setActiveCategory] = useState('Men');
  const [activeOverviewTab, setActiveOverviewTab] = useState('home'); // home, contact, about
  const [activeOrdersTab, setActiveOrdersTab] = useState('success'); // success, reject
  const [products, setProducts] = useState([]);
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [globalSearchResults, setGlobalSearchResults] = useState([]);
  const [globalSearchLoading, setGlobalSearchLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [imageInputType, setImageInputType] = useState('url'); // 'url' or 'upload'
  const [uploadedImage, setUploadedImage] = useState('');
  
  // Home page management
  const [banners, setBanners] = useState([]);
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [bannerForm, setBannerForm] = useState({
    title: '',
    subtitle: '',
    image: '',
    cta_text: '',
    cta_link: '',
    is_active: true,
    order: 0
  });

  // Contact page management
  const [contactInfo, setContactInfo] = useState({
    address: 'Jain Watch Company, Main Market, New Delhi, India - 110001',
    phone: '+91 9927058909',
    email: 'jainwatchcompany1975@gmail.com',
    whatsapp: '919927058909',
    store_hours: {
      regular_days: 'Tuesday - Sunday',
      regular_time_open: '9:00 AM',
      regular_time_close: '8:00 PM',
      monday_status: 'closed', // 'closed', 'open', 'varies'
      monday_time_open: '',
      monday_time_close: '',
      monday_note: 'Usually Closed',
      special_notice: '', // For festivals or special closures
      show_notice: false
    }
  });
  const [contactSaving, setContactSaving] = useState(false);

  // About page management
  const [aboutInfo, setAboutInfo] = useState({
    title: 'About Jain Watch Company',
    description: 'Founded in 1975, Jain Watch Company has been at the forefront of luxury watch retail in India for nearly five decades.',
    mission: '',
    vision: ''
  });
  const [aboutSaving, setAboutSaving] = useState(false);

  // Brand management
  const [brands, setBrands] = useState([]);
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [brandForm, setBrandForm] = useState({
    name: '',
    category: '',
    subcategory: '',
    logo_url: '',
    description: '',
    is_active: true
  });
  const [brandLogoInputType, setBrandLogoInputType] = useState('url'); // 'url' or 'upload'
  const [brandLogoUploading, setBrandLogoUploading] = useState(false);

  // Featured Brands management (Authorized Retailers)
  const [featuredBrands, setFeaturedBrands] = useState([]);
  const [showFeaturedBrandModal, setShowFeaturedBrandModal] = useState(false);
  const [editingFeaturedBrand, setEditingFeaturedBrand] = useState(null);
  const [featuredBrandForm, setFeaturedBrandForm] = useState({
    name: '',
    logo_url: '',
    link: '',
    order: 0,
    is_active: true
  });
  const [featuredBrandLogoInputType, setFeaturedBrandLogoInputType] = useState('url'); // 'url' or 'upload'
  const [featuredBrandLogoUploading, setFeaturedBrandLogoUploading] = useState(false);

  // Shop Categories management (Shop by Category section)
  const [shopCategories, setShopCategories] = useState([]);
  const [showShopCategoryModal, setShowShopCategoryModal] = useState(false);
  const [editingShopCategory, setEditingShopCategory] = useState(null);
  const [shopCategoryForm, setShopCategoryForm] = useState({
    name: '',
    image: '',
    link: '',
    order: 0,
    is_active: true
  });

  // Collections management (Best Selling, Luxury, etc.)
  const [collections, setCollections] = useState([]);
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [collectionForm, setCollectionForm] = useState({
    name: '',
    display_name: '',
    order: 0,
    is_active: true
  });

  // Shipping rules management
  const [shippingRules, setShippingRules] = useState([]);
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [editingShippingRule, setEditingShippingRule] = useState(null);
  const [shippingForm, setShippingForm] = useState({
    name: '',
    min_order_amount: 0,
    max_order_amount: '',
    shipping_charge: 0,
    priority: 0,
    is_active: true
  });

  // Shop category image upload state
  const [categoryImageInputType, setCategoryImageInputType] = useState('url');
  const [categoryImageUploading, setCategoryImageUploading] = useState(false);
  const [bannerImageInputType, setBannerImageInputType] = useState('url');
  const [bannerImageUploading, setBannerImageUploading] = useState(false);

  const [productForm, setProductForm] = useState({
    name: '',
    brand: '',
    category: 'Men',
    collection: 'Best Selling',
    price: '',
    original_price: '',
    description: '',
    image: '',
    stock: '',
    features: '',
    color: '',
    type: '',
    subcategory: '',
    model_number: '',
    slug: ''
  });

  // Media slots state - 6 slots: 0=main image, 1-4=additional images, 5=video
  const [mediaSlots, setMediaSlots] = useState([
    { url: '', type: 'image', inputType: 'url', uploading: false }, // Main image
    { url: '', type: 'image', inputType: 'url', uploading: false }, // Additional 1
    { url: '', type: 'image', inputType: 'url', uploading: false }, // Additional 2
    { url: '', type: 'image', inputType: 'url', uploading: false }, // Additional 3
    { url: '', type: 'image', inputType: 'url', uploading: false }, // Additional 4
    { url: '', type: 'video', inputType: 'url', uploading: false }, // Video
  ]);

  // Color variants state - for Smart Watches and Wall Clocks only
  const [colorVariants, setColorVariants] = useState([]);
  const MAX_COLOR_VARIANTS = 8;

  // Helper to create empty media slots for a color variant
  const createEmptyVariantMediaSlots = () => [
    { url: '', type: 'image', inputType: 'url', uploading: false }, // Main image
    { url: '', type: 'image', inputType: 'url', uploading: false }, // Additional 1
    { url: '', type: 'image', inputType: 'url', uploading: false }, // Additional 2
    { url: '', type: 'image', inputType: 'url', uploading: false }, // Additional 3
    { url: '', type: 'image', inputType: 'url', uploading: false }, // Additional 4
    { url: '', type: 'video', inputType: 'url', uploading: false }, // Video
  ];

  // Email settings state - per-status templates
  const defaultTemplate = (status) => ({ subject: '', body: '' });
  const [emailSettings, setEmailSettings] = useState({
    custom_message: 'Thank you for your purchase! Your order will be delivered soon.',
    confirmed_template: defaultTemplate('confirmed'),
    shipped_template: defaultTemplate('shipped'),
    delivered_template: defaultTemplate('delivered'),
    rejected_template: defaultTemplate('rejected'),
  });
  const [emailSettingsLoading, setEmailSettingsLoading] = useState(false);
  const [activeEmailTab, setActiveEmailTab] = useState('confirmed');

  const categoryTabs = [
    { id: 'dashboard', label: 'Dashboard', icon: Package },
    { 
      id: 'watches', 
      label: 'Watches', 
      icon: Package,
      subcategories: ['Men', 'Women', 'Kids', 'Unisex']
    },
    { id: 'wall-clocks', label: 'Wall Clocks', icon: Package, category: 'Wall Clocks' },
    { 
      id: 'accessories', 
      label: 'Accessories', 
      icon: Package,
      category: 'Accessories',
      subcategories: ['Perfumes', 'Stop Watches', 'Calculators', 'Alarm']
    },
    { id: 'smart-watches', label: 'Smart Watches', icon: Package, category: 'Smart Watches' },
    { id: 'overview', label: 'Overview', icon: Package },
    { id: 'orders', label: 'Orders', icon: ShoppingBag },
    { id: 'reviews', label: 'Reviews', icon: Star },
    { id: 'promo-codes', label: 'Promo Codes', icon: Tag },
    { id: 'shipping', label: 'Shipping', icon: Package },
    { id: 'email-settings', label: 'Email Settings', icon: Package },
  ];

  const overviewTabs = [
    { id: 'home', label: 'Home Page' },
    { id: 'contact', label: 'Contact' },
    { id: 'about', label: 'About Us' }
  ];

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [activeTab, activeCategory, activeOverviewTab]);

  // Auto-refresh Orders tab every 30s so new orders (from payments) and stock updates appear without manual refresh
  useEffect(() => {
    if (activeTab !== 'orders') return;
    const interval = setInterval(() => {
      fetchData();
    }, 30000);
    return () => clearInterval(interval);
  }, [activeTab]);

  // Global product search across all categories (Watches, Wall Clocks, Accessories, Smart Watches)
  useEffect(() => {
    const query = globalSearchQuery.trim();
    if (!query) {
      setGlobalSearchResults([]);
      setGlobalSearchLoading(false);
      return;
    }
    setGlobalSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/products`, { params: { search: query } });
        setGlobalSearchResults(res.data);
      } catch (err) {
        console.error('Global search error:', err);
        setGlobalSearchResults([]);
      } finally {
        setGlobalSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [globalSearchQuery]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'dashboard') {
        // Fetch dashboard stats
        const response = await axios.get(`${API}/admin/stats`, getAuthHeaders());
        setStats(response.data);
      } else if (activeTab === 'overview' && activeOverviewTab === 'home') {
        // Fetch banners, featured brands, shop categories, and collections
        const [bannersRes, featuredBrandsRes, shopCategoriesRes, collectionsRes] = await Promise.all([
          axios.get(`${API}/banners`),
          axios.get(`${API}/featured-brands?active_only=false`),
          axios.get(`${API}/shop-categories?active_only=false`),
          axios.get(`${API}/collections?active_only=false`)
        ]);
        setBanners(bannersRes.data);
        setFeaturedBrands(featuredBrandsRes.data);
        setShopCategories(shopCategoriesRes.data);
        setCollections(collectionsRes.data);
      } else if (activeTab === 'overview' && activeOverviewTab === 'contact') {
        // Fetch contact info
        const response = await axios.get(`${API}/admin/contact-info`, getAuthHeaders());
        setContactInfo({
          address: response.data.address || '',
          phone: response.data.phone || '',
          email: response.data.email || '',
          whatsapp: response.data.whatsapp || '',
          store_hours: response.data.store_hours || {
            regular_days: 'Tuesday - Sunday',
            regular_time_open: '9:00 AM',
            regular_time_close: '8:00 PM',
            monday_status: 'closed',
            monday_time_open: '',
            monday_time_close: '',
            monday_note: 'Usually Closed',
            special_notice: '',
            show_notice: false
          }
        });
      } else if (activeTab === 'overview' && activeOverviewTab === 'about') {
        // Fetch about info
        const response = await axios.get(`${API}/admin/about-info`, getAuthHeaders());
        setAboutInfo({
          title: response.data.title || '',
          description: response.data.description || '',
          mission: response.data.mission || '',
          vision: response.data.vision || ''
        });
      } else if (activeTab === 'watches') {
        const [productsRes, brandsRes] = await Promise.all([
          axios.get(`${API}/products?category=${activeCategory}`),
          axios.get(`${API}/admin/brands?category=Watches`, getAuthHeaders())
        ]);
        setProducts(productsRes.data);
        setBrands(brandsRes.data);
      } else if (activeTab === 'wall-clocks') {
        const [productsRes, brandsRes] = await Promise.all([
          axios.get(`${API}/products?category=Wall Clocks`),
          axios.get(`${API}/admin/brands?category=Wall Clocks`, getAuthHeaders())
        ]);
        setProducts(productsRes.data);
        setBrands(brandsRes.data);
      } else if (activeTab === 'accessories') {
        const [productsRes, brandsRes] = await Promise.all([
          axios.get(`${API}/products?category=Accessories&subcategory=${activeCategory}`),
          axios.get(`${API}/admin/brands?category=Accessories&subcategory=${activeCategory}`, getAuthHeaders())
        ]);
        setProducts(productsRes.data);
        setBrands(brandsRes.data);
      } else if (activeTab === 'smart-watches') {
        const [productsRes, brandsRes] = await Promise.all([
          axios.get(`${API}/products?category=Smart Watches`),
          axios.get(`${API}/admin/brands?category=Smart Watches`, getAuthHeaders())
        ]);
        setProducts(productsRes.data);
        setBrands(brandsRes.data);
      } else if (activeTab === 'orders') {
        const response = await axios.get(`${API}/orders`, getAuthHeaders());
        setOrders(response.data);
      } else if (activeTab === 'shipping') {
        const response = await axios.get(`${API}/admin/shipping-rules`, getAuthHeaders());
        setShippingRules(response.data);
      } else if (activeTab === 'email-settings') {
        const response = await axios.get(`${API}/admin/email-settings`, getAuthHeaders());
        setEmailSettings(response.data);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const handleProductFormChange = (e) => {
    setProductForm({
      ...productForm,
      [e.target.name]: e.target.value
    });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please upload an image smaller than 5MB",
          variant: "destructive"
        });
        return;
      }

      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImage(reader.result);
        setProductForm({
          ...productForm,
          image: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const resetProductForm = () => {
    setProductForm({
      name: '',
      brand: '',
      category: 'Men',
      collection: 'Best Selling',
      price: '',
      original_price: '',
      description: '',
      image: '',
      stock: '',
      features: '',
      color: '',
      type: '',
      subcategory: '',
      model_number: '',
      slug: ''
    });
    setMediaSlots([
      { url: '', type: 'image', inputType: 'url', uploading: false },
      { url: '', type: 'image', inputType: 'url', uploading: false },
      { url: '', type: 'image', inputType: 'url', uploading: false },
      { url: '', type: 'image', inputType: 'url', uploading: false },
      { url: '', type: 'image', inputType: 'url', uploading: false },
      { url: '', type: 'video', inputType: 'url', uploading: false },
    ]);
    setColorVariants([]);
    setEditingProduct(null);
    setShowProductModal(false);
    setImageInputType('url');
    setUploadedImage('');
  };

  // Handle media slot URL change
  const handleMediaSlotUrlChange = (index, url) => {
    const newSlots = [...mediaSlots];
    newSlots[index].url = url;
    setMediaSlots(newSlots);
    
    // Update main image in form for backward compatibility
    if (index === 0) {
      setProductForm(prev => ({ ...prev, image: url }));
    }
  };

  // Handle media slot input type toggle
  const handleMediaSlotInputTypeChange = (index, inputType) => {
    const newSlots = [...mediaSlots];
    newSlots[index].inputType = inputType;
    setMediaSlots(newSlots);
  };

  // Handle media file upload
  const handleMediaFileUpload = async (index, file) => {
    if (!file) return;
    
    const isVideo = index === 5;
    const maxSize = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024; // 50MB for video, 5MB for images
    
    if (file.size > maxSize) {
      toast({
        title: "File too large",
        description: `Maximum size is ${isVideo ? '50MB' : '5MB'}`,
        variant: "destructive"
      });
      return;
    }
    
    // Validate file type
    const allowedImageTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const allowedVideoTypes = ['video/mp4'];
    
    if (isVideo && !allowedVideoTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please upload an MP4 video",
        variant: "destructive"
      });
      return;
    }
    
    if (!isVideo && !allowedImageTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please upload JPG, PNG, or WEBP image",
        variant: "destructive"
      });
      return;
    }
    
    // Set uploading state
    const newSlots = [...mediaSlots];
    newSlots[index].uploading = true;
    setMediaSlots(newSlots);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Store relative URL (works across domains after deployment)
      const relativeUrl = response.data.url;
      
      // Update slot with uploaded URL
      const updatedSlots = [...mediaSlots];
      updatedSlots[index].url = relativeUrl;
      updatedSlots[index].uploading = false;
      setMediaSlots(updatedSlots);
      
      // Update main image for backward compatibility
      if (index === 0) {
        setProductForm(prev => ({ ...prev, image: relativeUrl }));
      }
      
      toast({ title: "File uploaded successfully" });
    } catch (error) {
      const updatedSlots = [...mediaSlots];
      updatedSlots[index].uploading = false;
      setMediaSlots(updatedSlots);
      
      toast({
        title: "Upload failed",
        description: error.response?.data?.detail || "Failed to upload file",
        variant: "destructive"
      });
    }
  };

  // Clear media slot
  const clearMediaSlot = (index) => {
    const newSlots = [...mediaSlots];
    newSlots[index].url = '';
    setMediaSlots(newSlots);
    
    if (index === 0) {
      setProductForm(prev => ({ ...prev, image: '' }));
    }
  };

  // Color Variant Handlers
  const addColorVariant = () => {
    if (colorVariants.length >= MAX_COLOR_VARIANTS) {
      toast({
        title: "Maximum variants reached",
        description: `You can only add up to ${MAX_COLOR_VARIANTS} color variants`,
        variant: "destructive"
      });
      return;
    }
    setColorVariants([
      ...colorVariants,
      {
        color_name: '',
        price: '',
        original_price: '',
        stock: '',
        mediaSlots: createEmptyVariantMediaSlots()
      }
    ]);
  };

  const removeColorVariant = (index) => {
    const newVariants = colorVariants.filter((_, i) => i !== index);
    setColorVariants(newVariants);
  };

  const updateColorVariant = (index, field, value) => {
    const newVariants = [...colorVariants];
    newVariants[index][field] = value;
    setColorVariants(newVariants);
  };

  const handleVariantMediaSlotUrlChange = (variantIndex, slotIndex, url) => {
    const newVariants = [...colorVariants];
    newVariants[variantIndex].mediaSlots[slotIndex].url = url;
    setColorVariants(newVariants);
  };

  const handleVariantMediaSlotInputTypeChange = (variantIndex, slotIndex, inputType) => {
    const newVariants = [...colorVariants];
    newVariants[variantIndex].mediaSlots[slotIndex].inputType = inputType;
    setColorVariants(newVariants);
  };

  const handleVariantMediaFileUpload = async (variantIndex, slotIndex, file) => {
    if (!file) return;
    
    const isVideo = slotIndex === 5;
    const maxSize = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024;
    
    if (file.size > maxSize) {
      toast({
        title: "File too large",
        description: `Maximum size is ${isVideo ? '50MB' : '5MB'}`,
        variant: "destructive"
      });
      return;
    }
    
    const allowedImageTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const allowedVideoTypes = ['video/mp4'];
    
    if (isVideo && !allowedVideoTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please upload an MP4 video",
        variant: "destructive"
      });
      return;
    }
    
    if (!isVideo && !allowedImageTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please upload JPG, PNG, or WEBP image",
        variant: "destructive"
      });
      return;
    }
    
    // Set uploading state
    const newVariants = [...colorVariants];
    newVariants[variantIndex].mediaSlots[slotIndex].uploading = true;
    setColorVariants(newVariants);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Store relative URL (works across domains after deployment)
      const relativeUrl = response.data.url;
      
      const updatedVariants = [...colorVariants];
      updatedVariants[variantIndex].mediaSlots[slotIndex].url = relativeUrl;
      updatedVariants[variantIndex].mediaSlots[slotIndex].uploading = false;
      setColorVariants(updatedVariants);
      
      toast({ title: "File uploaded successfully" });
    } catch (error) {
      const updatedVariants = [...colorVariants];
      updatedVariants[variantIndex].mediaSlots[slotIndex].uploading = false;
      setColorVariants(updatedVariants);
      
      toast({
        title: "Upload failed",
        description: error.response?.data?.detail || "Failed to upload file",
        variant: "destructive"
      });
    }
  };

  const clearVariantMediaSlot = (variantIndex, slotIndex) => {
    const newVariants = [...colorVariants];
    newVariants[variantIndex].mediaSlots[slotIndex].url = '';
    setColorVariants(newVariants);
  };

  // Check if color variants are enabled for current category
  const isColorVariantsEnabled = () => {
    return activeTab === 'smart-watches' || activeTab === 'wall-clocks';
  };

  // Check if product has color variants
  const hasColorVariants = colorVariants.length > 0;

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    
    // If no color variants, validate global image is provided
    if (!hasColorVariants && !mediaSlots[0].url) {
      toast({
        title: "Error",
        description: "Main product image is required",
        variant: "destructive"
      });
      return;
    }
    
    // If color variants exist, validate each variant has at least main image
    if (hasColorVariants) {
      for (let i = 0; i < colorVariants.length; i++) {
        if (!colorVariants[i].color_name.trim()) {
          toast({
            title: "Error",
            description: `Color variant ${i + 1}: Color name is required`,
            variant: "destructive"
          });
          return;
        }
        if (!colorVariants[i].price) {
          toast({
            title: "Error",
            description: `Color variant ${i + 1}: Price is required`,
            variant: "destructive"
          });
          return;
        }
        if (!colorVariants[i].mediaSlots[0].url) {
          toast({
            title: "Error",
            description: `Color variant ${i + 1}: Main image is required`,
            variant: "destructive"
          });
          return;
        }
      }
    }
    
    try {
      let productData;
      
      if (hasColorVariants) {
        // Build color_variants array
        const formattedVariants = colorVariants.map(variant => {
          const variantMedia = variant.mediaSlots
            .map((slot, index) => ({
              url: slot.url,
              type: slot.type,
              order: index
            }))
            .filter(m => m.url);
          
          return {
            color_name: variant.color_name.trim(),
            price: parseFloat(variant.price),
            original_price: variant.original_price ? parseFloat(variant.original_price) : null,
            stock: parseInt(variant.stock) || 0,
            media: variantMedia
          };
        });
        
        // Use first variant's first image as main product image for backward compatibility
        const mainImage = formattedVariants[0]?.media[0]?.url || '';
        
        productData = {
          name: productForm.name,
          brand: productForm.brand,
          category: productForm.category,
          collection: productForm.collection,
          description: productForm.description,
          features: productForm.features.split(',').map(f => f.trim()).filter(f => f),
          type: productForm.type || null,
          subcategory: productForm.subcategory || null,
          model_number: productForm.model_number || null,
          slug: productForm.slug || null,
          // Set default values that will be overridden by variants
          price: formattedVariants[0]?.price || 0,
          original_price: formattedVariants[0]?.original_price || null,
          stock: formattedVariants[0]?.stock || 0,
          image: mainImage,
          images: [],
          media: formattedVariants[0]?.media || [],
          color: null,
          color_variants: formattedVariants
        };
      } else {
        // Build media array from slots (original logic)
        const media = mediaSlots
          .map((slot, index) => ({
            url: slot.url,
            type: slot.type,
            order: index
          }))
          .filter(m => m.url);
        
        productData = {
          ...productForm,
          image: mediaSlots[0].url,
          price: parseFloat(productForm.price),
          original_price: productForm.original_price ? parseFloat(productForm.original_price) : null,
          stock: parseInt(productForm.stock),
          features: productForm.features.split(',').map(f => f.trim()).filter(f => f),
          images: media.filter(m => m.type === 'image').map(m => m.url),
          media: media,
          color: productForm.color || null,
          type: productForm.type || null,
          subcategory: productForm.subcategory || null,
          model_number: productForm.model_number || null,
          slug: productForm.slug || null,
          color_variants: []
        };
      }

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, productData, getAuthHeaders());
        toast({ title: "Product updated successfully" });
      } else {
        await axios.post(`${API}/products`, productData, getAuthHeaders());
        toast({ title: "Product created successfully" });
      }

      resetProductForm();
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save product",
        variant: "destructive"
      });
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      brand: product.brand,
      category: product.category,
      collection: product.collection,
      price: product.price.toString(),
      original_price: product.original_price ? product.original_price.toString() : '',
      description: product.description,
      image: product.image,
      stock: product.stock.toString(),
      features: product.features.join(', '),
      color: product.color || '',
      type: product.type || '',
      subcategory: product.subcategory || '',
      model_number: product.model_number || '',
      slug: product.slug || ''
    });
    
    // Load existing media into slots
    const newSlots = [
      { url: '', type: 'image', inputType: 'url', uploading: false },
      { url: '', type: 'image', inputType: 'url', uploading: false },
      { url: '', type: 'image', inputType: 'url', uploading: false },
      { url: '', type: 'image', inputType: 'url', uploading: false },
      { url: '', type: 'image', inputType: 'url', uploading: false },
      { url: '', type: 'video', inputType: 'url', uploading: false },
    ];
    
    // Load color variants if they exist
    if (product.color_variants && product.color_variants.length > 0) {
      const loadedVariants = product.color_variants.map(variant => {
        const variantMediaSlots = createEmptyVariantMediaSlots();
        if (variant.media && variant.media.length > 0) {
          variant.media.forEach(m => {
            if (m.order >= 0 && m.order < 6) {
              variantMediaSlots[m.order] = {
                url: m.url,
                type: m.type,
                inputType: 'url',
                uploading: false
              };
            }
          });
        }
        return {
          color_name: variant.color_name || '',
          price: variant.price ? variant.price.toString() : '',
          original_price: variant.original_price ? variant.original_price.toString() : '',
          stock: variant.stock ? variant.stock.toString() : '0',
          mediaSlots: variantMediaSlots
        };
      });
      setColorVariants(loadedVariants);
    } else {
      setColorVariants([]);
      
      // Only load global media if no color variants
      if (product.media && product.media.length > 0) {
        // Load from new media array
        product.media.forEach(m => {
          if (m.order >= 0 && m.order < 6) {
            newSlots[m.order] = { 
              url: m.url, 
              type: m.type, 
              inputType: 'url', 
              uploading: false 
            };
          }
        });
      } else if (product.image) {
        // Backward compatibility: load from single image field
        newSlots[0].url = product.image;
        
        // Also load from legacy images array if available
        if (product.images && product.images.length > 1) {
          product.images.slice(1, 5).forEach((img, idx) => {
            newSlots[idx + 1].url = img;
          });
        }
      }
    }
    
    setMediaSlots(newSlots);
    setImageInputType('url');
    setUploadedImage('');
    setShowProductModal(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await axios.delete(`${API}/products/${productId}`, getAuthHeaders());
      toast({ title: "Product deleted successfully" });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete product",
        variant: "destructive"
      });
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}`, 
        { status: newStatus },
        getAuthHeaders()
      );
      
      // Refetch orders so cancelled orders move to the Reject tab automatically
      // and any stock-restore effects propagate to dashboard/products on next visit.
      fetchData();
      
      toast({ 
        title: "Order status updated", 
        description: `Status changed to ${newStatus}. Customer email sent automatically.`
      });
    } catch (error) {
      console.error('Error updating order status:', error);
      toast({
        title: "Error",
        description: "Failed to update order status",
        variant: "destructive"
      });
    }
  };

  // Brand Management Handlers
  const handleBrandFormChange = (e) => {
    setBrandForm({
      ...brandForm,
      [e.target.name]: e.target.value
    });
  };

  // Handle brand logo file upload
  const handleBrandLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please upload an image smaller than 5MB",
        variant: "destructive"
      });
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please upload JPG, PNG, or WEBP image",
        variant: "destructive"
      });
      return;
    }

    setBrandLogoUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Store relative URL (works across domains after deployment)
      const relativeUrl = response.data.url;
      
      setBrandForm({
        ...brandForm,
        logo_url: relativeUrl
      });
      
      toast({ title: "Logo uploaded successfully" });
    } catch (error) {
      toast({
        title: "Upload failed",
        description: error.response?.data?.detail || "Failed to upload file",
        variant: "destructive"
      });
    } finally {
      setBrandLogoUploading(false);
    }
  };

  // Handle featured brand logo file upload
  const handleFeaturedBrandLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please upload an image smaller than 5MB",
        variant: "destructive"
      });
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please upload JPG, PNG, or WEBP image",
        variant: "destructive"
      });
      return;
    }

    setFeaturedBrandLogoUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Store relative URL (works across domains after deployment)
      const relativeUrl = response.data.url;
      
      setFeaturedBrandForm({
        ...featuredBrandForm,
        logo_url: relativeUrl
      });
      
      toast({ title: "Logo uploaded successfully" });
    } catch (error) {
      toast({
        title: "Upload failed",
        description: error.response?.data?.detail || "Failed to upload file",
        variant: "destructive"
      });
    } finally {
      setFeaturedBrandLogoUploading(false);
    }
  };

  const handleSaveBrand = async (e) => {
    e.preventDefault();
    
    try {
      if (editingBrand) {
        await axios.put(`${API}/admin/brands/${editingBrand.id}`, brandForm, getAuthHeaders());
        toast({ title: "Brand updated successfully" });
      } else {
        // Set category based on active tab
        const categoryMap = {
          'watches': 'Watches',
          'wall-clocks': 'Wall Clocks',
          'accessories': 'Accessories',
          'smart-watches': 'Smart Watches'
        };
        const brandData = {
          ...brandForm,
          category: categoryMap[activeTab] || 'Watches'
        };
        
        // Set subcategory for Accessories
        if (activeTab === 'accessories' && activeCategory) {
          brandData.subcategory = activeCategory;
        }
        
        await axios.post(`${API}/admin/brands`, brandData, getAuthHeaders());
        toast({ title: "Brand added successfully" });
      }
      
      setShowBrandModal(false);
      setEditingBrand(null);
      setBrandForm({ name: '', category: '', subcategory: '', logo_url: '', description: '', is_active: true });
      setBrandLogoInputType('url');
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save brand",
        variant: "destructive"
      });
    }
  };

  const handleEditBrand = (brand) => {
    setEditingBrand(brand);
    setBrandForm({
      name: brand.name,
      category: brand.category,
      subcategory: brand.subcategory || '',
      logo_url: brand.logo_url || '',
      description: brand.description || '',
      is_active: brand.is_active
    });
    setShowBrandModal(true);
  };

  const handleDeleteBrand = async (brandId) => {
    if (!window.confirm('Are you sure you want to delete this brand?')) return;

    try {
      await axios.delete(`${API}/admin/brands/${brandId}`, getAuthHeaders());
      toast({ title: "Brand deleted successfully" });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete brand",
        variant: "destructive"
      });
    }
  };

  // Email Settings Handler
  const handleSaveEmailSettings = async (e) => {
    e.preventDefault();
    setEmailSettingsLoading(true);

    try {
      await axios.put(
        `${API}/admin/email-settings`,
        {
          custom_message: emailSettings.custom_message,
          confirmed_template: emailSettings.confirmed_template,
          shipped_template: emailSettings.shipped_template,
          delivered_template: emailSettings.delivered_template,
          rejected_template: emailSettings.rejected_template,
        },
        getAuthHeaders()
      );
      toast({ title: "Email templates saved successfully" });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update email settings",
        variant: "destructive"
      });
    } finally {
      setEmailSettingsLoading(false);
    }
  };

  // Shipping Rules Handlers
  const resetShippingForm = () => {
    setShippingForm({
      name: '',
      min_order_amount: 0,
      max_order_amount: '',
      shipping_charge: 0,
      priority: 0,
      is_active: true
    });
    setEditingShippingRule(null);
  };

  const handleEditShippingRule = (rule) => {
    setEditingShippingRule(rule);
    setShippingForm({
      name: rule.name,
      min_order_amount: rule.min_order_amount || 0,
      max_order_amount: rule.max_order_amount || '',
      shipping_charge: rule.shipping_charge || 0,
      priority: rule.priority || 0,
      is_active: rule.is_active !== false
    });
    setShowShippingModal(true);
  };

  const handleSaveShippingRule = async (e) => {
    e.preventDefault();
    
    const ruleData = {
      name: shippingForm.name,
      min_order_amount: parseFloat(shippingForm.min_order_amount) || 0,
      max_order_amount: shippingForm.max_order_amount ? parseFloat(shippingForm.max_order_amount) : null,
      shipping_charge: parseFloat(shippingForm.shipping_charge) || 0,
      priority: parseInt(shippingForm.priority) || 0,
      is_active: shippingForm.is_active
    };

    try {
      if (editingShippingRule) {
        await axios.put(
          `${API}/admin/shipping-rules/${editingShippingRule.id}`,
          ruleData,
          getAuthHeaders()
        );
        toast({ title: "Shipping rule updated successfully" });
      } else {
        await axios.post(
          `${API}/admin/shipping-rules`,
          ruleData,
          getAuthHeaders()
        );
        toast({ title: "Shipping rule created successfully" });
      }
      setShowShippingModal(false);
      resetShippingForm();
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save shipping rule",
        variant: "destructive"
      });
    }
  };

  const handleDeleteShippingRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this shipping rule?')) return;

    try {
      await axios.delete(`${API}/admin/shipping-rules/${ruleId}`, getAuthHeaders());
      toast({ title: "Shipping rule deleted successfully" });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete shipping rule",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Toaster />

      {/* Header */}
      <header className="bg-black text-white shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">
                <span className="text-yellow-500">JAIN</span> WATCH - Admin
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <a href="/" className="text-gray-300 hover:text-white transition-colors">
                View Store
              </a>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 bg-red-500 hover:bg-red-600 px-4 py-2 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex space-x-1 overflow-x-auto">
            {categoryTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.subcategories) {
                    setActiveCategory(tab.subcategories[0]);
                  }
                }}
                className={`py-4 px-6 border-b-2 font-semibold transition-colors whitespace-nowrap flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-yellow-500 text-yellow-600'
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Dashboard Overview */}
        {activeTab === 'dashboard' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Dashboard Overview</h2>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
              </div>
            ) : stats ? (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 text-sm">Total Products</p>
                        <p className="text-3xl font-bold mt-2">{stats.total_products}</p>
                      </div>
                      <Package className="w-12 h-12 opacity-50" />
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100 text-sm">Total Orders</p>
                        <p className="text-3xl font-bold mt-2">{stats.total_orders}</p>
                      </div>
                      <ShoppingBag className="w-12 h-12 opacity-50" />
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100 text-sm">Total Brands</p>
                        <p className="text-3xl font-bold mt-2">{stats.total_brands}</p>
                      </div>
                      <Package className="w-12 h-12 opacity-50" />
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-orange-100 text-sm">Contact Forms</p>
                        <p className="text-3xl font-bold mt-2">{stats.total_contacts}</p>
                      </div>
                      <Package className="w-12 h-12 opacity-50" />
                    </div>
                  </div>
                </div>

                {/* Category Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-bold mb-4">Products by Category</h3>
                    <div className="space-y-3">
                      {Object.entries(stats.categories).map(([cat, count]) => (
                        <div key={cat} className="flex justify-between items-center">
                          <span className="text-gray-600">{cat}</span>
                          <span className="bg-gray-100 px-3 py-1 rounded-full font-semibold">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-bold mb-4">Low Stock Alert</h3>
                    {stats.low_stock.length === 0 ? (
                      <p className="text-gray-500">All products have sufficient stock</p>
                    ) : (
                      <div className="space-y-2">
                        {stats.low_stock.map((product) => (
                          <div key={product.id} className="flex justify-between items-center text-sm">
                            <span className="text-gray-700">{product.name}</span>
                            <span className="text-red-600 font-semibold">{product.stock} left</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {/* Overview Section with Subtabs */}
        {activeTab === 'overview' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Website Overview Management</h2>
            
            {/* Overview Subtabs */}
            <div className="flex space-x-2 mb-6 border-b">
              {overviewTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveOverviewTab(tab.id)}
                  className={`px-6 py-3 font-semibold border-b-2 transition-colors ${
                    activeOverviewTab === tab.id
                      ? 'border-yellow-500 text-yellow-600'
                      : 'border-transparent text-gray-600 hover:text-gray-800'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Products Section (Watches, Wall Clocks, Accessories, Smart Watches) */}
        {(activeTab === 'watches' || activeTab === 'wall-clocks' || activeTab === 'accessories' || activeTab === 'smart-watches') && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold">
                  {activeTab === 'watches' && 'Watches'}
                  {activeTab === 'wall-clocks' && 'Wall Clocks'}
                  {activeTab === 'accessories' && 'Accessories'}
                  {activeTab === 'smart-watches' && 'Smart Watches'}
                  {' '}({products.length})
                </h2>
                
                {/* Subcategory Tabs */}
                {(activeTab === 'watches' || activeTab === 'accessories') && (
                  <div className="flex space-x-2 mt-4">
                    {categoryTabs.find(t => t.id === activeTab)?.subcategories?.map((subcat) => (
                      <button
                        key={subcat}
                        onClick={() => setActiveCategory(subcat)}
                        className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                          activeCategory === subcat
                            ? 'bg-yellow-500 text-black'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {subcat}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={() => {
                  setProductForm({
                    ...productForm,
                    category: activeTab === 'watches' ? activeCategory : 
                             activeTab === 'wall-clocks' ? 'Wall Clocks' :
                             activeTab === 'accessories' ? 'Accessories' :
                             'Smart Watches',
                    subcategory: activeTab === 'accessories' ? activeCategory : ''
                  });
                  setShowProductModal(true);
                }}
                className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold px-6 py-3 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <Plus className="w-5 h-5" />
                <span>Add Product</span>
              </button>
            </div>

            {/* Brand Management Section */}
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800">Manage Brands</h3>
                <button
                  onClick={() => {
                    setBrandForm({ name: '', category: '', logo_url: '', description: '', is_active: true });
                    setEditingBrand(null);
                    setShowBrandModal(true);
                  }}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Brand</span>
                </button>
              </div>
              
              {brands.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No brands added yet</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {brands.map((brand) => (
                    <div key={brand.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          {brand.logo_url && (
                            <img src={resolveImageUrl(brand.logo_url)} alt={brand.name} className="h-12 w-auto mb-2 object-contain" />
                          )}
                          <h4 className="font-bold text-gray-800">{brand.name}</h4>
                          {brand.subcategory && (
                            <p className="text-xs text-yellow-600 font-semibold mt-1">{brand.subcategory}</p>
                          )}
                          {brand.description && (
                            <p className="text-sm text-gray-600 mt-1">{brand.description}</p>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditBrand(brand)}
                            className="text-blue-600 hover:text-blue-800 p-1"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteBrand(brand.id)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 mt-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          brand.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {brand.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Global Product Search (across all categories) */}
            <div className="bg-white rounded-lg shadow p-4 mb-6" data-testid="admin-global-search-container">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Global Search (Watches, Wall Clocks, Accessories, Smart Watches)
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={globalSearchQuery}
                  onChange={(e) => setGlobalSearchQuery(e.target.value)}
                  placeholder='Search by name, brand, color, type, category, or SKU (e.g. "titan black" or "NT1733KM03")'
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  data-testid="admin-global-search-input"
                />
                {globalSearchQuery && (
                  <button
                    onClick={() => setGlobalSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800 p-1"
                    title="Clear search"
                    data-testid="admin-global-search-clear"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {globalSearchQuery && (
                <p className="text-xs text-gray-500 mt-2" data-testid="admin-global-search-status">
                  {globalSearchLoading
                    ? 'Searching...'
                    : `${globalSearchResults.length} result${globalSearchResults.length === 1 ? '' : 's'} across all categories`}
                </p>
              )}
            </div>


            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Brand</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Color</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(() => {
                        const isSearching = globalSearchQuery.trim().length > 0;
                        const rows = isSearching ? globalSearchResults : [...products].sort((a, b) => a.stock - b.stock);
                        const categoryBadge = (product) => {
                          const cat = product.category;
                          if (cat === 'Wall Clocks') return { label: 'Wall Clock', cls: 'bg-purple-100 text-purple-800' };
                          if (cat === 'Accessories') return { label: 'Accessory', cls: 'bg-pink-100 text-pink-800' };
                          if (cat === 'Smart Watches') return { label: 'Smart Watch', cls: 'bg-blue-100 text-blue-800' };
                          return { label: 'Watch', cls: 'bg-yellow-100 text-yellow-800' };
                        };
                        if (isSearching && !globalSearchLoading && rows.length === 0) {
                          return (
                            <tr>
                              <td colSpan={8} className="px-6 py-8 text-center text-gray-500" data-testid="admin-global-search-empty">
                                No products match "{globalSearchQuery}"
                              </td>
                            </tr>
                          );
                        }
                        return rows.map((product) => {
                          const badge = categoryBadge(product);
                          return (
                        <tr key={product.id} className="hover:bg-gray-50" data-testid={`admin-product-row-${product.id}`}>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <img src={resolveImageUrl(product.image)} alt={product.name} className="w-12 h-12 object-cover rounded" />
                              <div className="flex flex-col">
                                <span className="font-medium text-sm">{product.name}</span>
                                {isSearching && (
                                  <span className={`mt-1 inline-block w-fit px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge.cls}`} data-testid={`admin-product-category-badge-${product.id}`}>
                                    {badge.label}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">{product.brand}</td>
                          <td className="px-6 py-4 text-sm">{product.subcategory || product.category}</td>
                          <td className="px-6 py-4 text-sm">{product.color || '-'}</td>
                          <td className="px-6 py-4 text-sm">{product.type || '-'}</td>
                          <td className="px-6 py-4">₹{product.price.toLocaleString()}</td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              product.stock > 10 ? 'bg-green-100 text-green-800' :
                              product.stock > 0 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {product.stock}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleEditProduct(product)}
                                className="text-blue-600 hover:text-blue-800 p-1"
                                title="Edit"
                                data-testid={`admin-product-edit-${product.id}`}
                              >
                                <Edit className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDeleteProduct(product.id)}
                                className="text-red-600 hover:text-red-800 p-1"
                                title="Delete"
                                data-testid={`admin-product-delete-${product.id}`}
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'email-settings' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">📧 Email Templates</h2>
            <div className="bg-white rounded-lg shadow-lg p-6 max-w-4xl">
              <p className="text-gray-600 mb-4">
                Customize the automated emails sent to customers when their order status changes. Use the variables below — they will be auto-filled with real order data.
              </p>

              {/* Available variables */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <p className="text-sm font-semibold text-yellow-900 mb-2">Available variables (click to copy):</p>
                <div className="flex flex-wrap gap-2">
                  {['{customer_name}','{order_id}','{product_details}','{quantity}','{total_amount}','{order_status}'].map(v => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => { navigator.clipboard.writeText(v); toast({ title: `Copied ${v}` }); }}
                      className="px-3 py-1 bg-white border border-yellow-300 rounded-full text-xs font-mono text-yellow-800 hover:bg-yellow-100 transition-colors"
                      data-testid={`email-var-${v.replace(/[{}]/g,'')}`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>

              {/* Template tabs */}
              <div className="flex flex-wrap gap-2 mb-4 border-b border-gray-200">
                {[
                  { key: 'confirmed', label: 'Order Confirmed', color: 'blue' },
                  { key: 'shipped', label: 'Order Shipped', color: 'purple' },
                  { key: 'delivered', label: 'Order Delivered', color: 'green' },
                  { key: 'rejected', label: 'Order Rejected', color: 'red' },
                ].map(tab => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveEmailTab(tab.key)}
                    data-testid={`email-tab-${tab.key}`}
                    className={`px-4 py-2 font-semibold text-sm rounded-t-lg border-b-2 transition-colors ${
                      activeEmailTab === tab.key
                        ? 'border-yellow-500 text-yellow-700 bg-yellow-50'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <form onSubmit={handleSaveEmailSettings} className="space-y-4">
                {(() => {
                  const tplKey = `${activeEmailTab}_template`;
                  const tpl = emailSettings[tplKey] || { subject: '', body: '' };
                  const updateTpl = (field, value) => {
                    setEmailSettings({
                      ...emailSettings,
                      [tplKey]: { ...tpl, [field]: value }
                    });
                  };
                  return (
                    <>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Email Subject
                        </label>
                        <input
                          type="text"
                          value={tpl.subject || ''}
                          onChange={(e) => updateTpl('subject', e.target.value)}
                          data-testid={`email-subject-${activeEmailTab}`}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent font-mono text-sm"
                          placeholder="e.g. Order Confirmed - #{order_id}"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Email Body
                        </label>
                        <textarea
                          value={tpl.body || ''}
                          onChange={(e) => updateTpl('body', e.target.value)}
                          data-testid={`email-body-${activeEmailTab}`}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent font-mono text-sm"
                          rows="12"
                          placeholder="Use {customer_name}, {order_id}, {product_details}, {quantity}, {total_amount}, {order_status}"
                          required
                        />
                        <p className="mt-2 text-xs text-gray-500">
                          Tip: <code className="bg-gray-100 px-1">{'{product_details}'}</code> renders as a styled table with all ordered items.
                        </p>
                      </div>
                    </>
                  );
                })()}

                <button
                  type="submit"
                  disabled={emailSettingsLoading}
                  data-testid="email-settings-save-btn"
                  className="bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-400 text-black font-semibold px-6 py-3 rounded-lg transition-colors"
                >
                  {emailSettingsLoading ? 'Saving...' : 'Save All Templates'}
                </button>
              </form>

              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="font-semibold text-gray-800 mb-3">ℹ️ How automated emails work</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>✓ When a customer's payment succeeds, the <b>Order Confirmed</b> email is sent automatically.</li>
                  <li>✓ When you change order status in the Orders tab to <b>Shipped</b>, <b>Delivered</b>, or <b>Rejected</b>, the matching email is sent instantly.</li>
                  <li>✓ All variables ({'{customer_name}'} etc.) are replaced with the real order's data.</li>
                  <li>✓ Stock is automatically reduced when payment is confirmed, and restored if the order is cancelled.</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'shipping' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">🚚 Shipping Rules</h2>
              <button
                onClick={() => {
                  resetShippingForm();
                  setShowShippingModal(true);
                }}
                data-testid="add-shipping-rule-btn"
                className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <Plus className="w-5 h-5" />
                <span>Add Shipping Rule</span>
              </button>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">💡 How Shipping Rules Work</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Rules are matched based on order total and priority (lower number = higher priority)</li>
                <li>• Set shipping charge to ₹0 for free shipping</li>
                <li>• Leave max amount empty for no upper limit (e.g., "Free shipping on orders above ₹2000")</li>
                <li>• Only active rules are applied to orders</li>
              </ul>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
              </div>
            ) : shippingRules.length === 0 ? (
              <div className="bg-white rounded-lg shadow-lg p-12 text-center">
                <p className="text-gray-500 text-lg">No shipping rules configured</p>
                <p className="text-gray-400 mt-2">Click "Add Shipping Rule" to create one</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Rule Name</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Order Range</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Shipping Charge</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Priority</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {shippingRules.map((rule) => (
                      <tr key={rule.id} className="hover:bg-gray-50" data-testid={`shipping-rule-${rule.id}`}>
                        <td className="px-6 py-4">
                          <span className="font-semibold text-gray-900">{rule.name}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-gray-600">
                            ₹{rule.min_order_amount?.toLocaleString() || 0}
                            {rule.max_order_amount ? ` - ₹${rule.max_order_amount.toLocaleString()}` : '+'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {rule.shipping_charge === 0 ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              FREE
                            </span>
                          ) : (
                            <span className="font-semibold text-gray-900">₹{rule.shipping_charge}</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {rule.priority}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            rule.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {rule.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEditShippingRule(rule)}
                              className="text-blue-600 hover:text-blue-800 p-1"
                              data-testid={`edit-shipping-${rule.id}`}
                            >
                              <Edit className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteShippingRule(rule.id)}
                              className="text-red-600 hover:text-red-800 p-1"
                              data-testid={`delete-shipping-${rule.id}`}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'reviews' && (
          <ReviewsManagement />
        )}

        {activeTab === 'promo-codes' && (
          <PromoCodeManagement />
        )}

        {activeTab === 'orders' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Orders Management</h2>
            
            {/* Orders Sub-tabs: Success / Reject */}
            <div className="flex space-x-2 mb-6 border-b">
              <button
                onClick={() => setActiveOrdersTab('success')}
                data-testid="orders-success-tab"
                className={`px-6 py-3 font-semibold border-b-2 transition-colors flex items-center space-x-2 ${
                  activeOrdersTab === 'success'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                }`}
              >
                <span className="w-3 h-3 rounded-full bg-green-500"></span>
                <span>Success ({orders.filter(o => o.status === 'confirmed' || o.status === 'shipped' || o.status === 'delivered').length})</span>
              </button>
              <button
                onClick={() => setActiveOrdersTab('reject')}
                data-testid="orders-reject-tab"
                className={`px-6 py-3 font-semibold border-b-2 transition-colors flex items-center space-x-2 ${
                  activeOrdersTab === 'reject'
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                }`}
              >
                <span className="w-3 h-3 rounded-full bg-red-500"></span>
                <span>Reject ({orders.filter(o => o.status === 'payment_failed' || o.status === 'cancelled').length})</span>
              </button>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
              </div>
            ) : (() => {
              // Filter orders based on active sub-tab
              const filteredOrders = activeOrdersTab === 'success'
                ? orders.filter(o => o.status === 'confirmed' || o.status === 'shipped' || o.status === 'delivered')
                : orders.filter(o => o.status === 'payment_failed' || o.status === 'cancelled');
              
              return filteredOrders.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-12 text-center">
                  <p className="text-gray-500 text-lg">
                    {activeOrdersTab === 'success' ? 'No successful orders yet' : 'No rejected/failed orders'}
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {filteredOrders.map((order) => (
                    <div key={order.id} className={`bg-white rounded-lg shadow-lg p-6 border-2 ${
                      activeOrdersTab === 'success' ? 'border-green-200' : 'border-red-200'
                    }`} data-testid={`order-card-${order.id.substring(0, 8)}`}>
                      {/* Order Header */}
                      <div className="flex justify-between items-start mb-4 pb-4 border-b">
                        <div>
                          <h3 className="font-bold text-xl text-gray-800">{order.customer_name}</h3>
                          <p className="text-sm text-gray-600 mt-1">📧 {order.customer_email}</p>
                          <p className="text-sm text-gray-600">📱 {order.customer_phone}</p>
                          <p className="text-xs text-gray-500 mt-2">
                            Order ID: {order.id.substring(0, 8)}... | {(() => {
                              const utcDate = new Date(order.created_at + 'Z');
                              return utcDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }) + ' ' + utcDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
                            })()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-3xl font-bold text-yellow-600">₹{order.total_amount.toLocaleString()}</p>
                          <div className="mt-3">
                            {activeOrdersTab === 'success' ? (
                              <select
                                value={order.status}
                                onChange={(e) => handleStatusChange(order.id, e.target.value)}
                                data-testid={`order-status-select-${order.id}`}
                                className={`px-4 py-2 rounded-full text-sm font-semibold border-2 cursor-pointer transition-all ${
                                  order.status === 'confirmed' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                                  order.status === 'shipped' ? 'bg-purple-100 text-purple-800 border-purple-300' :
                                  order.status === 'delivered' ? 'bg-green-100 text-green-800 border-green-300' :
                                  order.status === 'cancelled' ? 'bg-red-100 text-red-800 border-red-300' :
                                  'bg-gray-100 text-gray-800 border-gray-300'
                                }`}
                              >
                                <option value="confirmed">Confirmed</option>
                                <option value="shipped">Shipped</option>
                                <option value="delivered">Delivered</option>
                                <option value="cancelled">Reject / Cancel</option>
                              </select>
                            ) : (
                              <span className={`px-4 py-2 rounded-full text-sm font-semibold inline-block ${
                                order.status === 'payment_failed' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {order.status === 'payment_failed' ? 'Payment Failed' : 'Cancelled'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Order Details */}
                      <div className="mb-4">
                        <p className="text-sm text-gray-700 mb-2">
                          <strong className="text-gray-900">Delivery Address:</strong> {order.customer_address}
                        </p>
                        <p className="text-sm text-gray-700">
                          <strong className="text-gray-900">Payment Method:</strong> {order.payment_method === 'razorpay' ? 'Razorpay' : order.payment_method === 'contact_payment' ? 'Contact for Payment' : 'Cash on Delivery'}
                        </p>
                      </div>

                      {/* Ordered Products */}
                      <div className="border-t pt-4">
                        <h4 className="font-bold text-gray-800 mb-3">Ordered Products ({order.items.length})</h4>
                        <div className="space-y-3">
                          {order.items.map((item, index) => (
                            <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                              <img
                                src={resolveImageUrl(item.product_image)}
                                alt={item.product_name}
                                className="w-16 h-16 object-cover rounded border"
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/100?text=No+Image';
                                }}
                              />
                              <div className="flex-1">
                                <p className="font-semibold text-gray-800">{item.product_name}</p>
                                <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-gray-800">₹{(item.price * item.quantity).toLocaleString()}</p>
                                <p className="text-xs text-gray-500">₹{item.price.toLocaleString()} each</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {/* Home Page Management */}
        {activeTab === 'overview' && activeOverviewTab === 'home' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Home Page Banners ({banners.length})</h2>
              <button
                onClick={() => {
                  setBannerForm({
                    title: '',
                    subtitle: '',
                    image: '',
                    cta_text: '',
                    cta_link: '',
                    is_active: true,
                    order: banners.length
                  });
                  setEditingBanner(null);
                  setShowBannerModal(true);
                }}
                className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold px-6 py-3 rounded-lg flex items-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>Add Banner</span>
              </button>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6">
                {banners.map((banner) => (
                  <div key={banner.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="md:col-span-1">
                        <img src={resolveImageUrl(banner.image)} alt={banner.title} className="w-full h-full object-cover" />
                      </div>
                      <div className="md:col-span-2 p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="text-2xl font-bold mb-2">{banner.title}</h3>
                            <p className="text-gray-600 mb-2">{banner.subtitle}</p>
                            <div className="flex items-center space-x-4 text-sm">
                              <span className={`px-3 py-1 rounded-full ${banner.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                {banner.is_active ? 'Active' : 'Inactive'}
                              </span>
                              <span className="text-gray-500">Order: {banner.order}</span>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                setEditingBanner(banner);
                                setBannerForm({
                                  title: banner.title,
                                  subtitle: banner.subtitle,
                                  image: banner.image,
                                  cta_text: banner.cta_text,
                                  cta_link: banner.cta_link,
                                  is_active: banner.is_active,
                                  order: banner.order
                                });
                                setShowBannerModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-800 p-2"
                            >
                              <Edit className="w-5 h-5" />
                            </button>
                            <button
                              onClick={async () => {
                                if (window.confirm('Are you sure you want to delete this banner?')) {
                                  try {
                                    await axios.delete(`${API}/banners/${banner.id}`, getAuthHeaders());
                                    toast({ title: "Banner deleted successfully" });
                                    fetchData();
                                  } catch (error) {
                                    toast({ title: "Error deleting banner", variant: "destructive" });
                                  }
                                }
                              }}
                              className="text-red-600 hover:text-red-800 p-2"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                        <div className="border-t pt-4">
                          <p className="text-sm text-gray-600"><strong>CTA Text:</strong> {banner.cta_text}</p>
                          <p className="text-sm text-gray-600"><strong>CTA Link:</strong> {banner.cta_link}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Featured Brands Section - Authorized Retailers */}
            <div className="mt-12">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Authorized Retailers / Featured Brands ({featuredBrands.length})</h2>
                <button
                  onClick={() => {
                    setFeaturedBrandForm({
                      name: '',
                      logo_url: '',
                      link: '',
                      order: featuredBrands.length,
                      is_active: true
                    });
                    setEditingFeaturedBrand(null);
                    setShowFeaturedBrandModal(true);
                  }}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 py-3 rounded-lg flex items-center space-x-2"
                >
                  <Plus className="w-5 h-5" />
                  <span>Add Brand</span>
                </button>
              </div>

              <p className="text-gray-600 mb-4 text-sm">
                These brands appear in the "AUTHORIZED RETAILERS FOR 40+ BRANDS" section on the Home page. Add logo and they will display with horizontal scroll.
              </p>

              {featuredBrands.length === 0 ? (
                <div className="bg-gray-50 rounded-lg p-8 text-center">
                  <p className="text-gray-500">No featured brands yet. Add your first brand!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {featuredBrands.sort((a, b) => a.order - b.order).map((brand) => (
                    <div key={brand.id} className="bg-white rounded-lg shadow-md border border-gray-200 p-4 hover:shadow-lg transition-shadow">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          {brand.logo_url ? (
                            <img 
                              src={resolveImageUrl(brand.logo_url)} 
                              alt={brand.name} 
                              className="h-12 w-auto object-contain mb-2"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="h-12 w-full bg-gray-100 rounded flex items-center justify-center mb-2">
                              <span className="text-gray-400 text-xs">No Logo</span>
                            </div>
                          )}
                          <h4 className="font-bold text-gray-800 text-lg">{brand.name}</h4>
                        </div>
                        <div className="flex space-x-1">
                          <button
                            onClick={() => {
                              setEditingFeaturedBrand(brand);
                              setFeaturedBrandForm({
                                name: brand.name,
                                logo_url: brand.logo_url || '',
                                link: brand.link || '',
                                order: brand.order || 0,
                                is_active: brand.is_active
                              });
                              setShowFeaturedBrandModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 p-1"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={async () => {
                              if (window.confirm('Are you sure you want to delete this featured brand?')) {
                                try {
                                  await axios.delete(`${API}/featured-brands/${brand.id}`, getAuthHeaders());
                                  toast({ title: "Featured brand deleted successfully" });
                                  fetchData();
                                } catch (error) {
                                  toast({ title: "Error deleting featured brand", variant: "destructive" });
                                }
                              }
                            }}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2 pt-2 border-t">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          brand.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {brand.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <span className="text-xs text-gray-500">Order: {brand.order}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Shop Categories Section - Shop by Category */}
            <div className="mt-12">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Shop by Category ({shopCategories.length})</h2>
                <button
                  onClick={() => {
                    setShopCategoryForm({
                      name: '',
                      image: '',
                      link: '',
                      order: shopCategories.length,
                      is_active: true
                    });
                    setEditingShopCategory(null);
                    setShowShopCategoryModal(true);
                  }}
                  className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-3 rounded-lg flex items-center space-x-2"
                >
                  <Plus className="w-5 h-5" />
                  <span>Add Category</span>
                </button>
              </div>

              <p className="text-gray-600 mb-4 text-sm">
                Manage the "Shop by Category" section on the Home page. You can change the name and image for each category (Men, Women, Kids, Smart Watches, etc.).
              </p>

              {shopCategories.length === 0 ? (
                <div className="bg-gray-50 rounded-lg p-8 text-center">
                  <p className="text-gray-500">No categories yet. Add your first category!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {shopCategories.sort((a, b) => a.order - b.order).map((category) => (
                    <div key={category.id} className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                      <div className="aspect-square relative">
                        <img 
                          src={resolveImageUrl(category.image)} 
                          alt={category.name} 
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/400?text=No+Image';
                          }}
                        />
                        <div className="absolute top-2 right-2 flex space-x-1">
                          <button
                            onClick={() => {
                              setEditingShopCategory(category);
                              setShopCategoryForm({
                                name: category.name,
                                image: category.image || '',
                                link: category.link || '',
                                order: category.order || 0,
                                is_active: category.is_active
                              });
                              setShowShopCategoryModal(true);
                            }}
                            className="bg-white/90 hover:bg-white text-blue-600 p-2 rounded-full shadow"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={async () => {
                              if (window.confirm('Are you sure you want to delete this category?')) {
                                try {
                                  await axios.delete(`${API}/shop-categories/${category.id}`, getAuthHeaders());
                                  toast({ title: "Category deleted successfully" });
                                  fetchData();
                                } catch (error) {
                                  toast({ title: "Error deleting category", variant: "destructive" });
                                }
                              }
                            }}
                            className="bg-white/90 hover:bg-white text-red-600 p-2 rounded-full shadow"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="p-4">
                        <h4 className="font-bold text-gray-800 text-lg">{category.name}</h4>
                        <p className="text-xs text-gray-500 mt-1">Link: {category.link}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            category.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {category.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <span className="text-xs text-gray-500">Order: {category.order}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Collections Section */}
            <div className="mt-12">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Collections ({collections.length})</h2>
                <button
                  onClick={() => {
                    setCollectionForm({
                      name: '',
                      display_name: '',
                      order: collections.length,
                      is_active: true
                    });
                    setEditingCollection(null);
                    setShowCollectionModal(true);
                  }}
                  className="bg-purple-500 hover:bg-purple-600 text-white font-bold px-6 py-3 rounded-lg flex items-center space-x-2"
                >
                  <Plus className="w-5 h-5" />
                  <span>Add Collection</span>
                </button>
              </div>

              <p className="text-gray-600 mb-4 text-sm">
                Manage product collections shown on the Home page (Best Selling, Luxury, Best Budget, Astrological, etc.). You can add, edit, delete, or rename collections.
              </p>

              {collections.length === 0 ? (
                <div className="bg-gray-50 rounded-lg p-8 text-center">
                  <p className="text-gray-500">No collections yet. Add your first collection!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {collections.sort((a, b) => a.order - b.order).map((collection) => (
                    <div key={collection.id} className="bg-white rounded-lg shadow-md border border-gray-200 p-4 hover:shadow-lg transition-shadow">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-800 text-lg">{collection.name}</h4>
                          <p className="text-sm text-purple-600 font-semibold mt-1">{collection.display_name}</p>
                        </div>
                        <div className="flex space-x-1">
                          <button
                            onClick={() => {
                              setEditingCollection(collection);
                              setCollectionForm({
                                name: collection.name,
                                display_name: collection.display_name || '',
                                order: collection.order || 0,
                                is_active: collection.is_active
                              });
                              setShowCollectionModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 p-1"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={async () => {
                              if (window.confirm('Are you sure you want to delete this collection? Products in this collection will not be deleted.')) {
                                try {
                                  await axios.delete(`${API}/collections/${collection.id}`, getAuthHeaders());
                                  toast({ title: "Collection deleted successfully" });
                                  fetchData();
                                } catch (error) {
                                  toast({ title: "Error deleting collection", variant: "destructive" });
                                }
                              }
                            }}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2 pt-2 border-t">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          collection.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {collection.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <span className="text-xs text-gray-500">Order: {collection.order}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contact Page Management */}
        {activeTab === 'overview' && activeOverviewTab === 'contact' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Contact Information</h2>
            <div className="bg-white rounded-lg shadow-lg p-6">
              <form className="space-y-6" onSubmit={async (e) => {
                e.preventDefault();
                setContactSaving(true);
                try {
                  await axios.put(`${API}/admin/contact-info`, {
                    address: contactInfo.address,
                    phone: contactInfo.phone,
                    email: contactInfo.email,
                    whatsapp: contactInfo.whatsapp,
                    store_hours: contactInfo.store_hours
                  }, getAuthHeaders());
                  toast({ title: "Contact information saved successfully!" });
                } catch (error) {
                  toast({ title: "Error saving contact information", variant: "destructive" });
                } finally {
                  setContactSaving(false);
                }
              }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Phone Number</label>
                    <input
                      type="text"
                      value={contactInfo.phone}
                      onChange={(e) => setContactInfo({...contactInfo, phone: e.target.value})}
                      className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Email</label>
                    <input
                      type="email"
                      value={contactInfo.email}
                      onChange={(e) => setContactInfo({...contactInfo, email: e.target.value})}
                      className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">WhatsApp Number (without +)</label>
                    <input
                      type="text"
                      value={contactInfo.whatsapp}
                      onChange={(e) => setContactInfo({...contactInfo, whatsapp: e.target.value})}
                      placeholder="919927058909"
                      className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                  
                  {/* Store Hours Section */}
                  <div className="col-span-2 border-t pt-4 mt-2">
                    <h3 className="text-lg font-bold mb-4">Store Hours Management</h3>
                    
                    {/* Regular Hours (Tuesday - Sunday) */}
                    <div className="bg-gray-50 p-4 rounded-lg mb-4">
                      <h4 className="font-semibold mb-3">Regular Hours (Tuesday - Sunday)</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Opening Time</label>
                          <input
                            type="text"
                            value={contactInfo.store_hours?.regular_time_open || ''}
                            onChange={(e) => setContactInfo({...contactInfo, store_hours: {...contactInfo.store_hours, regular_time_open: e.target.value}})}
                            placeholder="9:00 AM"
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Closing Time</label>
                          <input
                            type="text"
                            value={contactInfo.store_hours?.regular_time_close || ''}
                            onChange={(e) => setContactInfo({...contactInfo, store_hours: {...contactInfo.store_hours, regular_time_close: e.target.value}})}
                            placeholder="8:00 PM"
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Monday Hours */}
                    <div className="bg-gray-50 p-4 rounded-lg mb-4">
                      <h4 className="font-semibold mb-3">Monday</h4>
                      <div className="mb-3">
                        <label className="block text-sm font-medium mb-2">Status</label>
                        <div className="flex gap-4">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="monday_status"
                              value="closed"
                              checked={contactInfo.store_hours?.monday_status === 'closed'}
                              onChange={(e) => setContactInfo({...contactInfo, store_hours: {...contactInfo.store_hours, monday_status: e.target.value}})}
                              className="mr-2"
                            />
                            Closed
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="monday_status"
                              value="open"
                              checked={contactInfo.store_hours?.monday_status === 'open'}
                              onChange={(e) => setContactInfo({...contactInfo, store_hours: {...contactInfo.store_hours, monday_status: e.target.value}})}
                              className="mr-2"
                            />
                            Open
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="monday_status"
                              value="varies"
                              checked={contactInfo.store_hours?.monday_status === 'varies'}
                              onChange={(e) => setContactInfo({...contactInfo, store_hours: {...contactInfo.store_hours, monday_status: e.target.value}})}
                              className="mr-2"
                            />
                            Varies
                          </label>
                        </div>
                      </div>
                      
                      {(contactInfo.store_hours?.monday_status === 'open' || contactInfo.store_hours?.monday_status === 'varies') && (
                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div>
                            <label className="block text-sm font-medium mb-1">Opening Time</label>
                            <input
                              type="text"
                              value={contactInfo.store_hours?.monday_time_open || ''}
                              onChange={(e) => setContactInfo({...contactInfo, store_hours: {...contactInfo.store_hours, monday_time_open: e.target.value}})}
                              placeholder="9:00 AM"
                              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Closing Time</label>
                            <input
                              type="text"
                              value={contactInfo.store_hours?.monday_time_close || ''}
                              onChange={(e) => setContactInfo({...contactInfo, store_hours: {...contactInfo.store_hours, monday_time_close: e.target.value}})}
                              placeholder="8:00 PM"
                              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                            />
                          </div>
                        </div>
                      )}
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Note (shown to customers)</label>
                        <input
                          type="text"
                          value={contactInfo.store_hours?.monday_note || ''}
                          onChange={(e) => setContactInfo({...contactInfo, store_hours: {...contactInfo.store_hours, monday_note: e.target.value}})}
                          placeholder="Usually Closed"
                          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        />
                      </div>
                    </div>
                    
                    {/* Special Notice (Festival/Holiday) */}
                    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold">Special Notice (Festival/Holiday)</h4>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={contactInfo.store_hours?.show_notice || false}
                            onChange={(e) => setContactInfo({...contactInfo, store_hours: {...contactInfo.store_hours, show_notice: e.target.checked}})}
                            className="mr-2"
                          />
                          Show Notice
                        </label>
                      </div>
                      <textarea
                        value={contactInfo.store_hours?.special_notice || ''}
                        onChange={(e) => setContactInfo({...contactInfo, store_hours: {...contactInfo.store_hours, special_notice: e.target.value}})}
                        placeholder="e.g., Closed on Diwali (Nov 12). Will reopen on Nov 14."
                        rows="2"
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">This notice will be displayed prominently when enabled</p>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Store Address</label>
                  <textarea
                    value={contactInfo.address}
                    onChange={(e) => setContactInfo({...contactInfo, address: e.target.value})}
                    rows="3"
                    className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={contactSaving}
                  className="bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-400 text-black font-bold px-8 py-3 rounded-lg"
                >
                  {contactSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* About Us Management */}
        {activeTab === 'overview' && activeOverviewTab === 'about' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">About Us Page</h2>
            <div className="bg-white rounded-lg shadow-lg p-6">
              <form className="space-y-6" onSubmit={async (e) => {
                e.preventDefault();
                setAboutSaving(true);
                try {
                  await axios.put(`${API}/admin/about-info`, {
                    title: aboutInfo.title,
                    description: aboutInfo.description,
                    mission: aboutInfo.mission,
                    vision: aboutInfo.vision
                  }, getAuthHeaders());
                  toast({ title: "About Us information saved successfully!" });
                } catch (error) {
                  toast({ title: "Error saving About Us information", variant: "destructive" });
                } finally {
                  setAboutSaving(false);
                }
              }}>
                <div>
                  <label className="block text-sm font-semibold mb-2">Page Title</label>
                  <input
                    type="text"
                    value={aboutInfo.title}
                    onChange={(e) => setAboutInfo({...aboutInfo, title: e.target.value})}
                    className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Company Story / Description</label>
                  <textarea
                    value={aboutInfo.description}
                    onChange={(e) => setAboutInfo({...aboutInfo, description: e.target.value})}
                    rows="6"
                    placeholder="Founded in 1975, Jain Watch Company..."
                    className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  ></textarea>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Our Mission</label>
                  <textarea
                    value={aboutInfo.mission}
                    onChange={(e) => setAboutInfo({...aboutInfo, mission: e.target.value})}
                    rows="4"
                    placeholder="Our mission is to..."
                    className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  ></textarea>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Our Vision</label>
                  <textarea
                    value={aboutInfo.vision}
                    onChange={(e) => setAboutInfo({...aboutInfo, vision: e.target.value})}
                    rows="4"
                    placeholder="Our vision is to..."
                    className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={aboutSaving}
                  className="bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-400 text-black font-bold px-8 py-3 rounded-lg"
                >
                  {aboutSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Product Modal */}
      {showProductModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 my-8 max-h-[90vh] overflow-y-auto relative">
            {/* Close Button */}
            <button
              onClick={resetProductForm}
              className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
              aria-label="Close"
              data-testid="product-modal-close-btn"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
            <h2 className="text-2xl font-bold mb-6">{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
            <form onSubmit={handleCreateProduct} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={productForm.name}
                    onChange={handleProductFormChange}
                    required
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">Brand *</label>
                  <select
                    name="brand"
                    value={productForm.brand}
                    onChange={handleProductFormChange}
                    required
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="">Select Brand</option>
                    {brands.map((brand) => (
                      <option key={brand.id} value={brand.name}>{brand.name}</option>
                    ))}
                  </select>
                  {brands.length === 0 && (
                    <p className="text-xs text-orange-600 mt-1">No brands found. Add brands first in the "Manage Brands" section above.</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">Model Number</label>
                  <input
                    type="text"
                    name="model_number"
                    value={productForm.model_number}
                    onChange={handleProductFormChange}
                    placeholder="e.g., A168WA-1WDF"
                    data-testid="product-form-model-number"
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Used in SEO-friendly URL, e.g. /product/casio-a168wa-1wdf</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">URL Slug</label>
                  <input
                    type="text"
                    name="slug"
                    value={productForm.slug}
                    onChange={handleProductFormChange}
                    placeholder="auto-generated from name + model"
                    data-testid="product-form-slug"
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave blank to auto-generate. Only lowercase letters, numbers and dashes.</p>
                </div>
                {/* Category - hidden for Accessories and Wall Clocks tabs */}
                {activeTab !== 'accessories' && activeTab !== 'wall-clocks' && (
                  <div>
                    <label className="block text-sm font-semibold mb-1">Category *</label>
                    <select
                      name="category"
                      value={productForm.category}
                      onChange={handleProductFormChange}
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    >
                      <option value="">Select Category</option>
                      {shopCategories.length > 0 ? (
                        shopCategories.map((cat) => (
                          <option key={cat.id} value={cat.name}>{cat.name}</option>
                        ))
                      ) : (
                        <>
                          <option value="Men">Men</option>
                          <option value="Women">Women</option>
                          <option value="Kids">Kids</option>
                          <option value="Unisex">Unisex</option>
                          <option value="Smart Watches">Smart Watches</option>
                          <option value="Wall Clocks">Wall Clocks</option>
                          <option value="Accessories">Accessories</option>
                        </>
                      )}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-semibold mb-1">Collection *</label>
                  <select
                    name="collection"
                    value={productForm.collection}
                    onChange={handleProductFormChange}
                    required
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="">Select Collection</option>
                    {collections.length > 0 ? (
                      collections.map((col) => (
                        <option key={col.id} value={col.name}>{col.name}</option>
                      ))
                    ) : (
                      <>
                        <option value="Best Selling">Best Selling</option>
                        <option value="Luxury">Luxury</option>
                        <option value="Best Budget">Best Budget</option>
                        <option value="Astrological">Astrological</option>
                      </>
                    )}
                  </select>
                </div>
                {/* Global Price/Stock fields - hidden when color variants exist */}
                {!hasColorVariants && (
                  <>
                    <div>
                      <label className="block text-sm font-semibold mb-1">Price *</label>
                      <input
                        type="number"
                        name="price"
                        value={productForm.price}
                        onChange={handleProductFormChange}
                        required={!hasColorVariants}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-1">Original Price</label>
                      <input
                        type="number"
                        name="original_price"
                        value={productForm.original_price}
                        onChange={handleProductFormChange}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-1">Stock *</label>
                      <input
                        type="number"
                        name="stock"
                        value={productForm.stock}
                        onChange={handleProductFormChange}
                        required={!hasColorVariants}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-1">Color</label>
                      <input
                        type="text"
                        name="color"
                        value={productForm.color}
                        onChange={handleProductFormChange}
                        placeholder="e.g., Black, Gold, Silver"
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-sm font-semibold mb-1">Type</label>
                  <input
                    type="text"
                    name="type"
                    value={productForm.type}
                    onChange={handleProductFormChange}
                    placeholder="e.g., Analog, Digital"
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
              </div>

              {/* Color Variants Section - Only for Smart Watches and Wall Clocks */}
              {isColorVariantsEnabled() && (
                <div className="col-span-2 border-t pt-4 mt-2">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h3 className="text-lg font-bold">Color Variants</h3>
                      <p className="text-sm text-gray-600">
                        Add different color options with individual pricing, stock, and images.
                      </p>
                    </div>
                    {colorVariants.length < MAX_COLOR_VARIANTS && (
                      <button
                        type="button"
                        onClick={addColorVariant}
                        className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-4 py-2 rounded-lg flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Add Color Variant
                      </button>
                    )}
                  </div>
                  
                  {colorVariants.length > 0 && (
                    <p className="text-xs text-orange-600 mb-4 bg-orange-50 p-2 rounded">
                      Note: When color variants are added, global Price, Original Price, Stock, and Media fields are disabled. Each variant has its own values.
                    </p>
                  )}
                  
                  {colorVariants.length === 0 ? (
                    <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <p className="text-gray-500 mb-2">No color variants added</p>
                      <p className="text-sm text-gray-400">Click "Add Color Variant" to add different color options for this product</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {colorVariants.map((variant, variantIndex) => (
                        <div key={variantIndex} className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                          <div className="flex justify-between items-center mb-4">
                            <h4 className="font-bold text-blue-800">Color Variant {variantIndex + 1}</h4>
                            <button
                              type="button"
                              onClick={() => removeColorVariant(variantIndex)}
                              className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm flex items-center gap-1"
                            >
                              <Trash2 className="w-4 h-4" />
                              Remove
                            </button>
                          </div>
                          
                          {/* Variant Fields */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                            <div>
                              <label className="block text-sm font-semibold mb-1">Color Name *</label>
                              <input
                                type="text"
                                value={variant.color_name}
                                onChange={(e) => updateColorVariant(variantIndex, 'color_name', e.target.value)}
                                placeholder="e.g., Midnight Black"
                                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-semibold mb-1">Price *</label>
                              <input
                                type="number"
                                value={variant.price}
                                onChange={(e) => updateColorVariant(variantIndex, 'price', e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-semibold mb-1">Original Price</label>
                              <input
                                type="number"
                                value={variant.original_price}
                                onChange={(e) => updateColorVariant(variantIndex, 'original_price', e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-semibold mb-1">Stock *</label>
                              <input
                                type="number"
                                value={variant.stock}
                                onChange={(e) => updateColorVariant(variantIndex, 'stock', e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                          
                          {/* Variant Media */}
                          <div className="border-t border-blue-200 pt-4">
                            <h5 className="font-semibold text-sm mb-3">Media for {variant.color_name || `Variant ${variantIndex + 1}`}</h5>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                              {variant.mediaSlots.map((slot, slotIndex) => {
                                const isVideo = slotIndex === 5;
                                const slotLabel = slotIndex === 0 ? 'Main *' : 
                                                 slotIndex <= 4 ? `Img ${slotIndex}` : 
                                                 'Video';
                                const acceptTypes = isVideo ? 'video/mp4' : 'image/jpeg,image/png,image/webp';
                                
                                return (
                                  <div 
                                    key={slotIndex} 
                                    className={`border rounded-lg p-2 ${slotIndex === 0 ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200 bg-white'}`}
                                  >
                                    <label className="block text-xs font-semibold mb-1">{slotLabel}</label>
                                    
                                    <div className="flex gap-1 mb-1">
                                      <button
                                        type="button"
                                        onClick={() => handleVariantMediaSlotInputTypeChange(variantIndex, slotIndex, 'url')}
                                        className={`px-2 py-0.5 text-[10px] rounded ${
                                          slot.inputType === 'url' ? 'bg-blue-500 text-white' : 'bg-gray-200'
                                        }`}
                                      >
                                        URL
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleVariantMediaSlotInputTypeChange(variantIndex, slotIndex, 'upload')}
                                        className={`px-2 py-0.5 text-[10px] rounded ${
                                          slot.inputType === 'upload' ? 'bg-blue-500 text-white' : 'bg-gray-200'
                                        }`}
                                      >
                                        Upload
                                      </button>
                                    </div>
                                    
                                    {slot.inputType === 'url' ? (
                                      <input
                                        type="text"
                                        value={slot.url}
                                        onChange={(e) => handleVariantMediaSlotUrlChange(variantIndex, slotIndex, e.target.value)}
                                        placeholder="URL..."
                                        className="w-full px-2 py-1 border rounded text-xs"
                                      />
                                    ) : (
                                      <div className="relative">
                                        <input
                                          type="file"
                                          accept={acceptTypes}
                                          onChange={(e) => handleVariantMediaFileUpload(variantIndex, slotIndex, e.target.files[0])}
                                          disabled={slot.uploading}
                                          className="w-full text-xs"
                                        />
                                        {slot.uploading && (
                                          <div className="absolute inset-0 bg-white/75 flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    
                                    {slot.url && (
                                      <div className="mt-1 relative">
                                        {isVideo ? (
                                          <video src={resolveImageUrl(slot.url)} className="w-full h-12 object-cover rounded" />
                                        ) : (
                                          <img src={resolveImageUrl(slot.url)} alt="" className="w-full h-12 object-cover rounded" />
                                        )}
                                        <button
                                          type="button"
                                          onClick={() => clearVariantMediaSlot(variantIndex, slotIndex)}
                                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]"
                                        >
                                          ×
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Product Media Section - 6 Slots - Hidden when color variants exist */}
              {!hasColorVariants && (
              <div className="col-span-2 border-t pt-4 mt-2">
                <h3 className="text-lg font-bold mb-3">Product Media (Images & Video)</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Add up to 5 images and 1 video. Main image is required. Media will be displayed in order when user swipes on product page.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {mediaSlots.map((slot, index) => {
                    const isVideo = index === 5;
                    const slotLabel = index === 0 ? 'Main Image *' : 
                                     index <= 4 ? `Additional Image ${index}` : 
                                     'Product Video';
                    const acceptTypes = isVideo ? 'video/mp4' : 'image/jpeg,image/png,image/webp';
                    
                    return (
                      <div 
                        key={index} 
                        className={`border rounded-lg p-3 ${index === 0 ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200'}`}
                      >
                        <label className="block text-sm font-semibold mb-2">
                          {slotLabel}
                          {index === 0 && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        
                        {/* Input Type Toggle */}
                        <div className="flex gap-2 mb-2">
                          <button
                            type="button"
                            onClick={() => handleMediaSlotInputTypeChange(index, 'url')}
                            className={`px-3 py-1 text-xs rounded-full transition-colors ${
                              slot.inputType === 'url' 
                                ? 'bg-yellow-500 text-black' 
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            URL
                          </button>
                          <button
                            type="button"
                            onClick={() => handleMediaSlotInputTypeChange(index, 'upload')}
                            className={`px-3 py-1 text-xs rounded-full transition-colors ${
                              slot.inputType === 'upload' 
                                ? 'bg-yellow-500 text-black' 
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            Upload
                          </button>
                        </div>
                        
                        {/* URL Input or File Upload */}
                        {slot.inputType === 'url' ? (
                          <input
                            type="text"
                            value={slot.url}
                            onChange={(e) => handleMediaSlotUrlChange(index, e.target.value)}
                            placeholder={isVideo ? 'https://example.com/video.mp4' : 'https://example.com/image.jpg'}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                          />
                        ) : (
                          <div className="relative">
                            <input
                              type="file"
                              accept={acceptTypes}
                              onChange={(e) => handleMediaFileUpload(index, e.target.files[0])}
                              disabled={slot.uploading}
                              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                            />
                            {slot.uploading && (
                              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500"></div>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Preview */}
                        {slot.url && (
                          <div className="mt-2 relative">
                            {isVideo ? (
                              <video 
                                src={resolveImageUrl(slot.url)} 
                                className="w-full h-24 object-cover rounded border"
                                controls
                              />
                            ) : (
                              <img 
                                src={resolveImageUrl(slot.url)} 
                                alt={`Preview ${index + 1}`} 
                                className="w-full h-24 object-cover rounded border"
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/150?text=Error';
                                }}
                              />
                            )}
                            <button
                              type="button"
                              onClick={() => clearMediaSlot(index)}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                              title="Remove"
                            >
                              ×
                            </button>
                          </div>
                        )}
                        
                        {/* Help text */}
                        <p className="text-xs text-gray-500 mt-1">
                          {isVideo ? 'MP4 only, max 50MB' : 'JPG, PNG, WEBP, max 5MB'}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
              )}

              <div className="col-span-2">
                <label className="block text-sm font-semibold mb-1">Description *</label>
                <textarea
                  name="description"
                  value={productForm.description}
                  onChange={handleProductFormChange}
                  required
                  rows="4"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Enter each point on a new line for bullet points display. Example:
Premium stainless steel case
Water resistant up to 50 meters
Sapphire crystal glass"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">Tip: Each new line will appear as a separate bullet point on the product page.</p>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-semibold mb-1">Features (comma-separated)</label>
                <input
                  type="text"
                  name="features"
                  value={productForm.features}
                  onChange={handleProductFormChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Water Resistant, Leather Strap, Date Display"
                />
              </div>
              <div className="col-span-2 flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetProductForm}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-black font-bold rounded-lg"
                >
                  {editingProduct ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Banner Modal */}
      {showBannerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 my-8 relative">
            {/* Close Button */}
            <button
              onClick={() => {
                setShowBannerModal(false);
                setEditingBanner(null);
              }}
              className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
              aria-label="Close"
              data-testid="banner-modal-close-btn"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
            <h2 className="text-2xl font-bold mb-6">{editingBanner ? 'Edit Banner' : 'Add New Banner'}</h2>
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                if (editingBanner) {
                  await axios.put(`${API}/banners/${editingBanner.id}`, bannerForm, getAuthHeaders());
                  toast({ title: "Banner updated successfully" });
                } else {
                  await axios.post(`${API}/banners`, bannerForm, getAuthHeaders());
                  toast({ title: "Banner created successfully" });
                }
                setShowBannerModal(false);
                setEditingBanner(null);
                fetchData();
              } catch (error) {
                toast({ title: "Error saving banner", variant: "destructive" });
              }
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1">Title *</label>
                <input
                  type="text"
                  value={bannerForm.title}
                  onChange={(e) => setBannerForm({...bannerForm, title: e.target.value})}
                  required
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Subtitle *</label>
                <input
                  type="text"
                  value={bannerForm.subtitle}
                  onChange={(e) => setBannerForm({...bannerForm, subtitle: e.target.value})}
                  required
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Image *</label>
                <div className="flex gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => setBannerImageInputType('url')}
                    className={`px-3 py-1 rounded text-sm font-medium ${
                      bannerImageInputType === 'url' 
                        ? 'bg-yellow-500 text-white' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    URL
                  </button>
                  <button
                    type="button"
                    onClick={() => setBannerImageInputType('upload')}
                    className={`px-3 py-1 rounded text-sm font-medium ${
                      bannerImageInputType === 'upload' 
                        ? 'bg-yellow-500 text-white' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Upload
                  </button>
                </div>
                
                {bannerImageInputType === 'url' ? (
                  <input
                    type="text"
                    value={bannerForm.image}
                    onChange={(e) => setBannerForm({...bannerForm, image: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="https://example.com/banner.jpg"
                    required={!bannerForm.image}
                  />
                ) : (
                  <div>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        
                        if (file.size > 5 * 1024 * 1024) {
                          toast({ title: "File too large", description: "Max 5MB", variant: "destructive" });
                          return;
                        }
                        
                        setBannerImageUploading(true);
                        try {
                          const formData = new FormData();
                          formData.append('file', file);
                          const response = await axios.post(`${API}/upload`, formData, {
                            ...getAuthHeaders(),
                            headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
                          });
                          // Store relative URL (works across domains after deployment)
                          const relativeUrl = response.data.url;
                          setBannerForm({...bannerForm, image: relativeUrl});
                          toast({ title: "Image uploaded successfully" });
                        } catch (error) {
                          toast({ title: "Upload failed", variant: "destructive" });
                        } finally {
                          setBannerImageUploading(false);
                        }
                      }}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      disabled={bannerImageUploading}
                    />
                    {bannerImageUploading && (
                      <p className="text-sm text-yellow-600 mt-1">Uploading...</p>
                    )}
                  </div>
                )}
                
                {bannerForm.image && (
                  <div className="mt-3">
                    <img src={resolveImageUrl(bannerForm.image)} alt="Preview" className="h-24 w-auto object-cover rounded border" />
                    <button
                      type="button"
                      onClick={() => setBannerForm({...bannerForm, image: ''})}
                      className="text-red-500 text-xs mt-1 hover:underline"
                    >
                      Remove image
                    </button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">CTA Button Text *</label>
                  <input
                    type="text"
                    value={bannerForm.cta_text}
                    onChange={(e) => setBannerForm({...bannerForm, cta_text: e.target.value})}
                    required
                    placeholder="Shop Now"
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">CTA Link *</label>
                  <input
                    type="text"
                    value={bannerForm.cta_link}
                    onChange={(e) => setBannerForm({...bannerForm, cta_link: e.target.value})}
                    required
                    placeholder="/products"
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">Order</label>
                  <input
                    type="number"
                    value={bannerForm.order}
                    onChange={(e) => setBannerForm({...bannerForm, order: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div>
                  <label className="flex items-center space-x-2 mt-6">
                    <input
                      type="checkbox"
                      checked={bannerForm.is_active}
                      onChange={(e) => setBannerForm({...bannerForm, is_active: e.target.checked})}
                      className="w-5 h-5"
                    />
                    <span className="font-semibold">Active</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowBannerModal(false);
                    setEditingBanner(null);
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-black font-bold rounded-lg"
                >
                  {editingBanner ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Featured Brand Modal */}
      {showFeaturedBrandModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 my-8 relative">
            {/* Close Button */}
            <button
              onClick={() => {
                setShowFeaturedBrandModal(false);
                setEditingFeaturedBrand(null);
                setFeaturedBrandLogoInputType('url');
              }}
              className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
              aria-label="Close"
              data-testid="featured-brand-modal-close-btn"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
            <h2 className="text-2xl font-bold mb-6">{editingFeaturedBrand ? 'Edit Featured Brand' : 'Add Featured Brand'}</h2>
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                if (editingFeaturedBrand) {
                  await axios.put(`${API}/featured-brands/${editingFeaturedBrand.id}`, featuredBrandForm, getAuthHeaders());
                  toast({ title: "Featured brand updated successfully" });
                } else {
                  await axios.post(`${API}/featured-brands`, featuredBrandForm, getAuthHeaders());
                  toast({ title: "Featured brand created successfully" });
                }
                setShowFeaturedBrandModal(false);
                setEditingFeaturedBrand(null);
                setFeaturedBrandLogoInputType('url');
                fetchData();
              } catch (error) {
                toast({ title: "Error saving featured brand", variant: "destructive" });
              }
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1">Brand Name (Optional)</label>
                <input
                  type="text"
                  value={featuredBrandForm.name}
                  onChange={(e) => setFeaturedBrandForm({...featuredBrandForm, name: e.target.value})}
                  placeholder="e.g., Casio, Titan, Fastrack"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Brand Logo</label>
                
                {/* Toggle between URL and Upload */}
                <div className="flex space-x-4 mb-3">
                  <button
                    type="button"
                    onClick={() => setFeaturedBrandLogoInputType('url')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      featuredBrandLogoInputType === 'url'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    URL
                  </button>
                  <button
                    type="button"
                    onClick={() => setFeaturedBrandLogoInputType('upload')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      featuredBrandLogoInputType === 'upload'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Upload File
                  </button>
                </div>

                {featuredBrandLogoInputType === 'url' ? (
                  <input
                    type="text"
                    value={featuredBrandForm.logo_url}
                    onChange={(e) => setFeaturedBrandForm({...featuredBrandForm, logo_url: e.target.value})}
                    placeholder="https://example.com/logo.png"
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFeaturedBrandLogoUpload}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={featuredBrandLogoUploading}
                    />
                    {featuredBrandLogoUploading && (
                      <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-lg">
                        <span className="text-sm text-gray-600">Uploading...</span>
                      </div>
                    )}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">If logo is provided, it will display above the brand name</p>
              </div>
              {featuredBrandForm.logo_url && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-600 mb-2">Logo Preview:</p>
                  <img 
                    src={resolveImageUrl(featuredBrandForm.logo_url)} 
                    alt="Logo preview" 
                    className="h-12 w-auto object-contain"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/100x50?text=Invalid+URL';
                    }}
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-semibold mb-1">Link (Optional)</label>
                <input
                  type="text"
                  value={featuredBrandForm.link}
                  onChange={(e) => setFeaturedBrandForm({...featuredBrandForm, link: e.target.value})}
                  placeholder="/products?brand=BrandName (auto-generated if empty)"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty to auto-generate link to products page</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">Display Order</label>
                  <input
                    type="number"
                    value={featuredBrandForm.order}
                    onChange={(e) => setFeaturedBrandForm({...featuredBrandForm, order: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="flex items-center space-x-2 mt-6">
                    <input
                      type="checkbox"
                      checked={featuredBrandForm.is_active}
                      onChange={(e) => setFeaturedBrandForm({...featuredBrandForm, is_active: e.target.checked})}
                      className="w-5 h-5"
                    />
                    <span className="font-semibold">Active</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowFeaturedBrandModal(false);
                    setEditingFeaturedBrand(null);
                    setFeaturedBrandLogoInputType('url');
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-lg"
                >
                  {editingFeaturedBrand ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Brand Modal */}
      {showBrandModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
            {/* Close Button */}
            <button
              onClick={() => {
                setShowBrandModal(false);
                setEditingBrand(null);
              }}
              className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors z-10"
              aria-label="Close"
              data-testid="brand-modal-close-btn"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-6">{editingBrand ? 'Edit Brand' : 'Add New Brand'}</h2>
              
              <form onSubmit={handleSaveBrand} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Brand Name</label>
                  <input
                    type="text"
                    name="name"
                    value={brandForm.name}
                    onChange={handleBrandFormChange}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none"
                    placeholder="e.g., Rolex, Casio"
                  />
                </div>

                {/* Show subcategory field if in Accessories tab */}
                {(activeTab === 'accessories' || editingBrand?.category === 'Accessories') && (
                  <div>
                    <label className="block text-sm font-semibold mb-2">Subcategory *</label>
                    <select
                      name="subcategory"
                      value={brandForm.subcategory}
                      onChange={handleBrandFormChange}
                      required
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none"
                    >
                      <option value="">Select Subcategory</option>
                      <option value="Perfumes">Perfumes</option>
                      <option value="Stop Watches">Stop Watches</option>
                      <option value="Calculators">Calculators</option>
                      <option value="Alarm">Alarm</option>
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold mb-2">Brand Logo</label>
                  
                  {/* Toggle between URL and Upload */}
                  <div className="flex space-x-4 mb-3">
                    <button
                      type="button"
                      onClick={() => setBrandLogoInputType('url')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        brandLogoInputType === 'url'
                          ? 'bg-yellow-500 text-black'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      URL
                    </button>
                    <button
                      type="button"
                      onClick={() => setBrandLogoInputType('upload')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        brandLogoInputType === 'upload'
                          ? 'bg-yellow-500 text-black'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Upload File
                    </button>
                  </div>

                  {brandLogoInputType === 'url' ? (
                    <input
                      type="text"
                      name="logo_url"
                      value={brandForm.logo_url}
                      onChange={handleBrandFormChange}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none"
                      placeholder="https://example.com/logo.png"
                    />
                  ) : (
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleBrandLogoUpload}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none"
                        disabled={brandLogoUploading}
                      />
                      {brandLogoUploading && (
                        <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-lg">
                          <span className="text-sm text-gray-600">Uploading...</span>
                        </div>
                      )}
                    </div>
                  )}

                  {brandForm.logo_url && (
                    <div className="mt-2">
                      <img src={resolveImageUrl(brandForm.logo_url)} alt="Brand logo preview" className="h-16 w-auto object-contain" />
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Description</label>
                  <textarea
                    name="description"
                    value={brandForm.description}
                    onChange={handleBrandFormChange}
                    rows="3"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none"
                    placeholder="Brief description about the brand"
                  ></textarea>
                </div>

                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={brandForm.is_active}
                      onChange={(e) => setBrandForm({ ...brandForm, is_active: e.target.checked })}
                      className="w-4 h-4 text-yellow-500 rounded focus:ring-yellow-500"
                    />
                    <span className="text-sm font-semibold">Active</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowBrandModal(false);
                      setEditingBrand(null);
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-lg"
                  >
                    {editingBrand ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Shop Category Modal */}
      {showShopCategoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">{editingShopCategory ? 'Edit Category' : 'Add New Category'}</h2>
                <button
                  onClick={() => {
                    setShowShopCategoryModal(false);
                    setEditingShopCategory(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  if (editingShopCategory) {
                    await axios.put(`${API}/shop-categories/${editingShopCategory.id}`, shopCategoryForm, getAuthHeaders());
                    toast({ title: "Category updated successfully" });
                  } else {
                    await axios.post(`${API}/shop-categories`, shopCategoryForm, getAuthHeaders());
                    toast({ title: "Category created successfully" });
                  }
                  setShowShopCategoryModal(false);
                  setEditingShopCategory(null);
                  fetchData();
                } catch (error) {
                  toast({ title: "Error saving category", description: error.response?.data?.detail, variant: "destructive" });
                }
              }} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Category Name *</label>
                  <input
                    type="text"
                    value={shopCategoryForm.name}
                    onChange={(e) => setShopCategoryForm({ ...shopCategoryForm, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="e.g., Men, Women, Kids"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Category Image *</label>
                  <div className="flex space-x-2 mb-3">
                    <button
                      type="button"
                      onClick={() => setCategoryImageInputType('url')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium ${
                        categoryImageInputType === 'url' 
                          ? 'bg-green-500 text-white' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      URL
                    </button>
                    <button
                      type="button"
                      onClick={() => setCategoryImageInputType('upload')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium ${
                        categoryImageInputType === 'upload' 
                          ? 'bg-green-500 text-white' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Upload
                    </button>
                  </div>
                  
                  {categoryImageInputType === 'url' ? (
                    <input
                      type="text"
                      value={shopCategoryForm.image}
                      onChange={(e) => setShopCategoryForm({ ...shopCategoryForm, image: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="https://example.com/image.jpg"
                      required={!shopCategoryForm.image}
                    />
                  ) : (
                    <div>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          
                          if (file.size > 5 * 1024 * 1024) {
                            toast({ title: "File too large", description: "Max 5MB", variant: "destructive" });
                            return;
                          }
                          
                          setCategoryImageUploading(true);
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            const response = await axios.post(`${API}/upload`, formData, {
                              ...getAuthHeaders(),
                              headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
                            });
                            // Store relative URL (works across domains after deployment)
                            const relativeUrl = response.data.url;
                            setShopCategoryForm({ ...shopCategoryForm, image: relativeUrl });
                            toast({ title: "Image uploaded successfully" });
                          } catch (error) {
                            toast({ title: "Upload failed", variant: "destructive" });
                          } finally {
                            setCategoryImageUploading(false);
                          }
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        disabled={categoryImageUploading}
                      />
                      {categoryImageUploading && (
                        <p className="text-sm text-green-600 mt-1">Uploading...</p>
                      )}
                    </div>
                  )}
                  
                  {shopCategoryForm.image && (
                    <div className="mt-3">
                      <img src={resolveImageUrl(shopCategoryForm.image)} alt="Preview" className="h-24 w-24 object-cover rounded border" />
                      <button
                        type="button"
                        onClick={() => setShopCategoryForm({ ...shopCategoryForm, image: '' })}
                        className="text-red-500 text-xs mt-1 hover:underline"
                      >
                        Remove image
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Link Path</label>
                  <input
                    type="text"
                    value={shopCategoryForm.link}
                    onChange={(e) => setShopCategoryForm({ ...shopCategoryForm, link: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="/products/Men (auto-generated if empty)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Display Order</label>
                  <input
                    type="number"
                    value={shopCategoryForm.order}
                    onChange={(e) => setShopCategoryForm({ ...shopCategoryForm, order: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    min="0"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="shopCategoryActive"
                    checked={shopCategoryForm.is_active}
                    onChange={(e) => setShopCategoryForm({ ...shopCategoryForm, is_active: e.target.checked })}
                    className="w-4 h-4 text-green-600 rounded"
                  />
                  <label htmlFor="shopCategoryActive" className="ml-2 text-sm font-medium text-gray-700">
                    Active (visible on home page)
                  </label>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowShopCategoryModal(false);
                      setEditingShopCategory(null);
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg"
                  >
                    {editingShopCategory ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Collection Modal */}
      {showCollectionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">{editingCollection ? 'Edit Collection' : 'Add New Collection'}</h2>
                <button
                  onClick={() => {
                    setShowCollectionModal(false);
                    setEditingCollection(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  if (editingCollection) {
                    await axios.put(`${API}/collections/${editingCollection.id}`, collectionForm, getAuthHeaders());
                    toast({ title: "Collection updated successfully" });
                  } else {
                    await axios.post(`${API}/collections`, collectionForm, getAuthHeaders());
                    toast({ title: "Collection created successfully" });
                  }
                  setShowCollectionModal(false);
                  setEditingCollection(null);
                  fetchData();
                } catch (error) {
                  toast({ title: "Error saving collection", description: error.response?.data?.detail, variant: "destructive" });
                }
              }} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Collection Name *</label>
                  <input
                    type="text"
                    value={collectionForm.name}
                    onChange={(e) => setCollectionForm({ ...collectionForm, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g., Best Selling, Luxury"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">This is used internally to match products</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Display Name *</label>
                  <input
                    type="text"
                    value={collectionForm.display_name}
                    onChange={(e) => setCollectionForm({ ...collectionForm, display_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g., BEST SELLING, OUR LUXURY"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">This is what customers see on the home page</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Display Order</label>
                  <input
                    type="number"
                    value={collectionForm.order}
                    onChange={(e) => setCollectionForm({ ...collectionForm, order: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    min="0"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="collectionActive"
                    checked={collectionForm.is_active}
                    onChange={(e) => setCollectionForm({ ...collectionForm, is_active: e.target.checked })}
                    className="w-4 h-4 text-purple-600 rounded"
                  />
                  <label htmlFor="collectionActive" className="ml-2 text-sm font-medium text-gray-700">
                    Active (visible on home page)
                  </label>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCollectionModal(false);
                      setEditingCollection(null);
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-purple-500 hover:bg-purple-600 text-white font-bold rounded-lg"
                  >
                    {editingCollection ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Shipping Rule Modal */}
      {showShippingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">{editingShippingRule ? 'Edit Shipping Rule' : 'Add Shipping Rule'}</h2>
                <button
                  onClick={() => {
                    setShowShippingModal(false);
                    resetShippingForm();
                  }}
                  className="text-gray-500 hover:text-gray-700"
                  data-testid="shipping-modal-close-btn"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <form onSubmit={handleSaveShippingRule} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Rule Name *</label>
                  <input
                    type="text"
                    value={shippingForm.name}
                    onChange={(e) => setShippingForm({ ...shippingForm, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="e.g., Free Shipping, Standard Delivery"
                    required
                    data-testid="shipping-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Min Order (₹) *</label>
                    <input
                      type="number"
                      value={shippingForm.min_order_amount}
                      onChange={(e) => setShippingForm({ ...shippingForm, min_order_amount: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="0"
                      min="0"
                      required
                      data-testid="shipping-min-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Max Order (₹)</label>
                    <input
                      type="number"
                      value={shippingForm.max_order_amount}
                      onChange={(e) => setShippingForm({ ...shippingForm, max_order_amount: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="No limit"
                      min="0"
                      data-testid="shipping-max-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave empty for no upper limit</p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Shipping Charge (₹) *</label>
                  <input
                    type="number"
                    value={shippingForm.shipping_charge}
                    onChange={(e) => setShippingForm({ ...shippingForm, shipping_charge: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="0"
                    min="0"
                    required
                    data-testid="shipping-charge-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Set to 0 for free shipping</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Priority</label>
                  <input
                    type="number"
                    value={shippingForm.priority}
                    onChange={(e) => setShippingForm({ ...shippingForm, priority: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="0"
                    min="0"
                    data-testid="shipping-priority-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Lower number = higher priority (checked first)</p>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="shippingActive"
                    checked={shippingForm.is_active}
                    onChange={(e) => setShippingForm({ ...shippingForm, is_active: e.target.checked })}
                    className="w-4 h-4 text-yellow-600 rounded"
                    data-testid="shipping-active-checkbox"
                  />
                  <label htmlFor="shippingActive" className="ml-2 text-sm font-medium text-gray-700">
                    Active (apply to orders)
                  </label>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowShippingModal(false);
                      resetShippingForm();
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    data-testid="shipping-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-black font-bold rounded-lg"
                    data-testid="shipping-save-btn"
                  >
                    {editingShippingRule ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
